import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Heading, VStack, FormControl, FormLabel, Select, Button, Card, CardBody, Text, useColorModeValue, HStack, Flex, Input, useToast, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Table, Thead, Tbody, Tr, Th, Td, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, List, ListItem, Tag, TagLabel, TagCloseButton, Collapse, Textarea, Switch, Checkbox, Divider } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import SearchableSelect from '../components/SearchableSelect';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import FormulaCalculator from '../services/formulaCalculator';
import { useCurrency } from '../contexts/CurrencyContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableSection from '../components/proposal/SortableSection';
import SortableItem from '../components/proposal/SortableItem';

// Inline field: label + control in a single row
const InlineField = ({ label, required = false, children, minLabelW = '140px' }) => (
  <HStack align="center" spacing={4} w="100%">
    <Box minW={minLabelW} w={minLabelW}>
      <Text fontWeight="semibold" textAlign="left">
        {label} {required ? '*' : ''}
      </Text>
    </Box>
    <Box flex={1}>{children}</Box>
  </HStack>
);

const ProposalCreate = () => {
  const { t, currentLanguage } = useLanguage();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Clients araması için bileşen içinden çağrı yapılacak
  const { data: templates } = useQuery('proposal_templates', () => api.get('/api/settings/templates').then(res => res.data.data.templates));
  const { data: sections } = useQuery('sections', () => api.get('/api/meta/sections').then(res => res.data.data.sections));
  const { data: units } = useQuery('units', () => api.get('/api/meta/units').then(res => {
    console.log('Units API response:', res.data);
    return res.data.data.units;
  }));
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [sectionBlocks, setSectionBlocks] = useState([]); // [{section_id, items: [{id,name,quantity,unit_price}]}]

  const addSectionBlock = () => {
    if (!selectedSectionId) return;
    const sec = sections?.find(s => String(s.id) === String(selectedSectionId));
    if (!sec) return;
    const nameEn = sec.name_en || 'Section';
    const nameDe = sec.name_de || sec.name_en || 'Section';
    const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
    setSectionBlocks(prev => [...prev, { 
      section_id: sec.id, 
      section_name: sectionName,
      name_en: nameEn,
      name_de: nameDe,
      margin: 0, // Default margin (%) for manually added sections
      items: [] 
    }]);
    setSelectedSectionId('');
  };

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle section reorder
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setSectionBlocks((sections) => {
      const oldIndex = sections.findIndex((_, i) => `section-${i}` === active.id);
      const newIndex = sections.findIndex((_, i) => `section-${i}` === over.id);
      return arrayMove(sections, oldIndex, newIndex);
    });
  };

  // Handle item reorder within a section
  const handleItemDragEnd = (sectionIndex) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setSectionBlocks((sections) => {
      const section = sections[sectionIndex];
      const oldIndex = section.items.findIndex((_, i) => `item-${sectionIndex}-${i}` === active.id);
      const newIndex = section.items.findIndex((_, i) => `item-${sectionIndex}-${i}` === over.id);
      
      const newItems = arrayMove(section.items, oldIndex, newIndex);
      return sections.map((s, i) => i === sectionIndex ? { ...s, items: newItems } : s);
    });
  };

  const [selection, setSelection] = useState({ client_id: '', client_name: '', client_company: '', template_name: '', template_id: '' });
  const [title, setTitle] = useState('');
  const [versionNote, setVersionNote] = useState('');
  const [version, setVersion] = useState('1');
  const [titleTouched, setTitleTouched] = useState(false);
  const isReady = Boolean(selection.client_id && selection.template_id);
  const [footerOpen, setFooterOpen] = useState(false);
  const [meta, setMeta] = useState({ project: '', subject: '', offer_status: '', in_charge: '', notes: '', footer_text: '', footer_signature_left: '', footer_signature_right: '' });
  const [proposalType, setProposalType] = useState('offer'); // 'offer' or 'proforma'
  const [showDetailedPricing, setShowDetailedPricing] = useState(true);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideTotal, setOverrideTotal] = useState('');

  // Minimal Rich Text Editor: supports bold
  const FooterEditor = ({ value, onChange }) => {
    const ref = useRef(null);
    const [html, setHtml] = useState(value || '');
    
    // value prop değiştiğinde html state'ini güncelle
    useEffect(() => {
      setHtml(value || '');
    }, [value]);
    const [isFocused, setIsFocused] = useState(false);
    const sanitizeClient = (html) => {
      if (!html) return '';
      
      let s = String(html);
      
      // Sadece metin varsa olduğu gibi döndür
      if (!/<[^>]*>/.test(s)) {
        return s;
      }
      
      // Basit HTML temizleme
      s = s.replace(/<\s*strong\s*>/gi, '<b>').replace(/<\s*\/\s*strong\s*>/gi, '</b>');
      s = s.replace(/<\s*span[^>]*font-weight\s*:\s*(bold|700|800|900)[^>]*>([\s\S]*?)<\s*\/\s*span\s*>/gi, '<b>$2</b>');
      s = s.replace(/<\s*(div|p)\s*>/gi, '').replace(/<\s*\/\s*(div|p)\s*>/gi, '<br>');
      s = s.replace(/<(?!\/?(b|br|ul|li)\b)[^>]*>/gi, '');
      s = s.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
      
      return s;
    };
    useEffect(() => {
      // Sadece editör focus değilken ve değer değiştiğinde DOM'u güncelle
      if (!isFocused && ref.current && ref.current.innerHTML !== (value || '')) {
        ref.current.innerHTML = value || '';
      }
    }, [value, isFocused, html]);
    const applyBold = () => {
      try {
        if (!ref.current) return;
        
        // Editörü focus yap
        ref.current.focus({ preventScroll: true });
        
        // Bold komutunu uygula
        document.execCommand('bold', false, null);
        
        // İçeriği güncelle
        const currentContent = ref.current.innerHTML;
        setHtml(currentContent);
      } catch (error) {
        console.warn('Bold apply error:', error);
      }
    };
    const onPaste = (e) => {
      try {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const html = (text || '').replace(/\r?\n/g, '<br>');
        document.execCommand('insertHTML', false, sanitizeClient(html));
        setHtml(sanitizeClient(ref.current.innerHTML));
      } catch (_) {}
    };
    const onKeyDown = () => {};
    return (
      <VStack align="stretch" spacing={2}>
        <HStack>
          <Button size="xs" onMouseDown={(e)=> e.preventDefault()} onClick={applyBold} fontWeight="bold">B</Button>
        </HStack>
        <Box
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={(e)=> { 
            const raw = e.currentTarget.innerHTML; 
            setHtml(raw);
            // onInput sırasında onChange çağırma - sadece onBlur'da çağır
          }}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onFocus={()=> setIsFocused(true)}
          onBlur={()=> { 
            setIsFocused(false); 
            // Mevcut içeriği al ve temizle
            const currentContent = ref.current ? ref.current.innerHTML : html;
            const sanitized = sanitizeClient(currentContent);
            setHtml(sanitized);
            onChange(sanitized);
          }}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          minH="140px"
          p={3}
          bg="white"
          fontSize="14px"
          lineHeight="1.4"
          _focusWithin={{ outline: 'none', boxShadow: '0 0 0 1px #3182ce' }}
        />
      </VStack>
    );
  };
  const { data: userNames } = useQuery('user_names', () => api.get('/api/users/names').then(res => res.data.data.users));

  const [editingUnits, setEditingUnits] = useState({}); // {sectionIndex_itemIndex: true}
  const [editingUsage, setEditingUsage] = useState(null); // Currently editing usage cell
  const [templateUsages, setTemplateUsages] = useState({}); // Template'den gelen usages
  const [itemUsages, setItemUsages] = useState({}); // {sectionIndex_itemIndex_usageKey: {ratio: 1.05}}
  const [formulaRules, setFormulaRules] = useState([]); // Template'e ait formula rules
  const [itemCalculations, setItemCalculations] = useState({}); // Hesaplama sonuçları
  const [rowContextMenu, setRowContextMenu] = useState({ open: false, x: 0, y: 0, sectionIndex: null, itemIndex: null });
  // Create item modal state
  const [createItemModal, setCreateItemModal] = useState({ isOpen: false, label: '', sectionIndex: null });
  const [subDescModal, setSubDescModal] = useState({ isOpen: false, sectionIndex: null, itemIndex: null });
  const [noteModal, setNoteModal] = useState({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
  const [subDescQuery, setSubDescQuery] = useState('');
  const [subDescOptions, setSubDescOptions] = useState([]);
  const [subDescLoading, setSubDescLoading] = useState(false);
  const [subDescInput, setSubDescInput] = useState('');
  const loadSubDescriptions = async (item) => {
    try {
      setSubDescLoading(true);
      const type = item.item_type ? item.item_type : (item.item_id ? 'product' : 'service');
      const idParam = type === 'service' ? (item.service_id || item.item_id || item.id || 0) : (item.item_id || 0);
      const { data } = await api.get(`/api/meta/sub-descriptions`, { params: { item_type: type, item_id: idParam, q: subDescQuery } });
      setSubDescOptions(data?.data?.items || []);
    } catch (_) {
      setSubDescOptions([]);
    } finally {
      setSubDescLoading(false);
    }
  };

  const openSubDesc = (sectionIndex, itemIndex) => {
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    setSubDescQuery('');
    setSubDescInput('');
    setSubDescOptions([]);
    setSubDescModal({ isOpen: true, sectionIndex, itemIndex });
    loadSubDescriptions(item);
  };

  const addSubDescription = async () => {
    const { sectionIndex, itemIndex } = subDescModal;
    if (subDescInput.trim().length === 0) return;
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    const type = item.item_type ? item.item_type : (item.item_id ? 'product' : 'service');
    const idParam = type === 'service' ? (item.service_id || item.item_id || item.id || 0) : (item.item_id || 0);
    try {
      await api.post('/api/meta/sub-descriptions', { item_type: type, item_id: idParam, sub_description: subDescInput.trim() });
      setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
        ...b,
        items: b.items.map((x, k) => k === itemIndex ? ({
          ...x,
          sub_descriptions: Array.isArray(x.sub_descriptions) ? [...x.sub_descriptions, subDescInput.trim()] : [subDescInput.trim()]
        }) : x)
      }) : b));
      setSubDescInput('');
      toast({ title: t('common.success'), status: 'success' });
      loadSubDescriptions(item);
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const removeSubDescription = async (id) => {
    try {
      await api.delete(`/api/meta/sub-descriptions/${id}`);
      const item = sectionBlocks[subDescModal.sectionIndex]?.items[subDescModal.itemIndex];
      loadSubDescriptions(item);
      toast({ title: t('common.success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };
  
  // Contractor modal states
  const [contractorModal, setContractorModal] = useState({ isOpen: false, sectionIndex: null, itemIndex: null });
  const [contractorItems, setContractorItems] = useState([]); // [{name, quantity, unit_price, total_price}]
  const [selectedContractor, setSelectedContractor] = useState('');
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorSuggestions, setContractorSuggestions] = useState([]);
  const [showContractorSuggestions, setShowContractorSuggestions] = useState(false);
  
  // New supplier modal state
  const [newSupplierModal, setNewSupplierModal] = useState(false);
  const [newSupplierForm, setNewSupplierForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    category: '',
    rating: '',
    notes: ''
  });
  
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();

  // Formula Calculator instance
  const formulaCalculator = new FormulaCalculator();

  // Calculate values using formula engine
  const calculateItemValues = async (item, sectionMargin, sectionIndex, itemIndex) => {
    // Set variables for calculation
    const variables = {
      qty: Number(item.quantity || 0),
      unit_price: Number(item.unit_price || 0),
      // yüzde olarak tutulan margin'i 1.xx'e dönüştürerek hesapla
      margin: Number((item.margin === 0 || item.margin === null || item.margin === undefined)
        ? ( (sectionMargin === 0 || sectionMargin === null || sectionMargin === undefined) ? 1.0 : (1 + (sectionMargin/100)) )
        : (1 + (item.margin/100))),
      // Add template usage ratios (with custom item ratios if available)
      ...Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
        // Convert usage_1 to usage1 for formula compatibility
        const normalizedKey = usageKey.replace('usage_', 'usage');
        const itemUsageKey = `${sectionIndex}_${itemIndex}_${usageKey}`;
        const customRatio = itemUsages[itemUsageKey]?.ratio;
        
        // Use custom ratio if available, otherwise use template default
        acc[`${normalizedKey}_ratio`] = customRatio !== undefined ? Number(customRatio) : Number(usage.ratio || 1);
        return acc;
      }, {})
    };

    formulaCalculator.setVariables(variables);

    // Use formula rules if available, otherwise use default template (ID: 1) formulas
    let formulas = formulaRules;
    if (formulas.length === 0) {
      formulas = await loadDefaultTemplateFormulas();
    }
    
    const results = formulaCalculator.executeFormulas(formulas);
    
    // Update variables with calculated usage results for next formula executions
    Object.entries(templateUsages).forEach(([usageKey, usage]) => {
      const normalizedKey = usageKey.replace('usage_', 'usage');
      const usageResult = results[`template.${normalizedKey}.rowResult`];
      if (usageResult !== undefined) {
        variables[`${normalizedKey}_rowResult`] = usageResult;
      }
    });
    
    // Re-execute formulas with updated variables (for dependent formulas)
    if (Object.keys(templateUsages).length > 0) {
      formulaCalculator.setVariables(variables);
      const updatedResults = formulaCalculator.executeFormulas(formulas);
      // Merge results, preferring updated values
      Object.assign(results, updatedResults);
    }

    // SPPU'yu 2 ondalık basamağa yuvarla (tutarlılık için)
    const rawSppu = results['table.sppu'] || 0;
    const sppu = Math.round(rawSppu * 100) / 100;

    // SLT'yi yuvarlanmış SPPU ile hesapla (25.00 × 72 = 1800.00, 25.004 × 72 = 1800.29 değil)
    const qty = Number(variables.qty || 0);
    const slt = Math.round((qty * sppu) * 100) / 100;

    // unit_price_x_amount ve final_price yuvarlanmış değerlerle hesapla (tutarlılık için)
    const unit_price = Number(variables.unit_price || 0);
    const unit_price_x_amount = Math.round((qty * unit_price) * 100) / 100;
    const final_price = slt; // final_price = slt (zaten yuvarlanmış)

    return {
      sppu,
      slt,
      final_price,
      unit_price_x_amount,
      // Calculate usage values
      usages: Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
        // Her zaman yuvarlanmış SLT üzerinden hesapla (tutarlılık için)
        // Formula engine ham değerlerle hesaplıyor, bu yüzden onun sonucunu kullanmıyoruz
        const ratio = Number(usage.ratio || 1);
        acc[usageKey] = Math.round((slt * ratio) * 100) / 100;
        return acc;
      }, {})
    };
  };

  // Templates backend'den geliyor
  // Helpers
  const formatDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    if (!isReady) return;
    const tplName = selection.template_name || templates?.find(t => String(t.id) === String(selection.template_id))?.name || '';
    const cliName = selection.client_company || selection.client_name || '';
    if (!cliName || !tplName) return;
    const today = formatDate(new Date());
    const generated = `${cliName} - ${tplName} - ${today} - v${version || '1'}`;
    if (!titleTouched) setTitle(generated);
  }, [isReady, selection.client_name, selection.template_name, selection.template_id, templates, version, titleTouched]);

  const createProposalMutation = useMutation(
    (payload) => api.post('/api/proposals', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proposals');
        toast({ title: t('common.success'), status: 'success' });
        try { localStorage.setItem('sidebar_collapsed','false'); window.dispatchEvent(new CustomEvent('sidebar:set-collapsed', { detail: false })); } catch {}
        navigate('/proposals');
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('messages.server_error'), status: 'error' });
      }
    }
  );

  // Auto-apply first template when available
  useEffect(() => {
    if (!templates || !Array.isArray(templates) || templates.length === 0) return;
    if (selection.template_id) return;
    const tpl = templates[0];
    setSelection(prev => ({ ...prev, template_id: String(tpl.id), template_name: tpl?.name || '' }));
    
    // Load template sections and usages from new JSON structure
    try {
      const templateData = tpl?.json || {};
      const rawSections = templateData.sections || [];
      const rawUsages = templateData.usages || {};
      
      // Set template usages
      setTemplateUsages(rawUsages);
      // Also load default notes into form meta
      if (typeof templateData.notes === 'string') {
        setMeta(prev => ({ ...prev, notes: templateData.notes }));
      }
      
      if (Array.isArray(rawSections) && rawSections.length > 0) {
        const mapped = rawSections.map((s) => {
          // Support both old format (name) and new format (name_en, name_de)
          const nameEn = s.name_en || s.name || s.section_name || s.title || 'Section';
          const nameDe = s.name_de || s.name || s.section_name || s.title || 'Section';
          const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
          return {
            section_id: s.section_id || s.id || null,
            section_name: sectionName,
            name_en: nameEn,
            name_de: nameDe,
            margin: Number(s.margin || 1.0), // Use section-specific margin
            items: []
          };
        });
        setSectionBlocks(mapped);
      } else {
        setSectionBlocks([]);
      }
    } catch (_) {
      setSectionBlocks([]);
      setTemplateUsages({});
    }

    // Load formula rules for this template
    loadFormulaRules(tpl.id);
  }, [templates, selection.template_id]);

  // Update section names when language changes
  useEffect(() => {
    if (sectionBlocks.length === 0) return;
    setSectionBlocks(prev => prev.map(block => {
      if (block.name_en || block.name_de) {
        const sectionName = currentLanguage === 'de' 
          ? (block.name_de || block.name_en || 'Section')
          : (block.name_en || block.name_de || 'Section');
        return { ...block, section_name: sectionName };
      }
      return block;
    }));
  }, [currentLanguage]);

  // Load formula rules for template
  const loadFormulaRules = async (templateId) => {
    try {
      const response = await api.get(`/api/formula-rules/template/${templateId}`);
      setFormulaRules(response.data.data.rules || []);
    } catch (error) {
      console.warn('Failed to load formula rules:', error);
      setFormulaRules([]); // Use default formulas
    }
  };

  // Load default template (ID: 1) formulas when current template has no formulas
  const loadDefaultTemplateFormulas = async () => {
    try {
      const response = await api.get('/api/formula-rules/template/1');
      return response.data.data.rules || [];
    } catch (error) {
      console.warn('Failed to load default template formulas:', error);
      return [];
    }
  };

  // Apply formula rules to items when formula rules change
  useEffect(() => {
    if (sectionBlocks.length > 0) {
      // Apply ALL formula rules to all items
      const updateItems = async () => {
        const updatedBlocks = await Promise.all(sectionBlocks.map(async (section, sectionIndex) => ({
          ...section,
          items: await Promise.all(section.items.map(async (item, itemIndex) => {
            // Use calculateItemValues function for proper formula execution
            const calculatedValues = await calculateItemValues(item, section.margin, sectionIndex, itemIndex);
            
            // Apply calculated values to item
            return {
              ...item,
              // Apply calculated values
              sppu: calculatedValues.sppu,
              slt: calculatedValues.slt,
              final_price: calculatedValues.final_price,
              unit_price_x_amount: calculatedValues.unit_price_x_amount,
              // Apply usage values
              usages: calculatedValues.usages
            };
          }))
        })));
        
        setSectionBlocks(updatedBlocks);
      };
      
      updateItems();
    }
  }, [formulaRules, sectionBlocks.length, templateUsages]);

  // Auto-recalculate when item values change
  useEffect(() => {
    if (sectionBlocks.length > 0) {
      // This will trigger recalculation when any item value changes
      // The calculateItemValues function will use the latest values
    }
  }, [sectionBlocks, formulaRules]);

  // Calculate all item values and store in state
  useEffect(() => {
    if (sectionBlocks.length > 0) {
      const calculateAllItems = async () => {
        const calculations = {};
        
        for (let sectionIndex = 0; sectionIndex < sectionBlocks.length; sectionIndex++) {
          const section = sectionBlocks[sectionIndex];
          for (let itemIndex = 0; itemIndex < section.items.length; itemIndex++) {
            const item = section.items[itemIndex];
            const key = `${sectionIndex}_${itemIndex}`;
            calculations[key] = await calculateItemValues(item, section.margin, sectionIndex, itemIndex);
          }
        }
        
        setItemCalculations(calculations);
      };
      
      calculateAllItems();
    }
  }, [sectionBlocks, formulaRules, templateUsages, itemUsages]);

  // Calculate average price per usage (live calculation)
  // Average = (SLT + usage_1 + usage_2 + ...) / (1 + number of usages)
  const averagePricePerUsage = useMemo(() => {
    if (sectionBlocks.length === 0) return 0;
    
    // Calculate total SLT
    const totalSLT = sectionBlocks.reduce((sum, section, sIdx) => {
      return sum + (section.items || []).reduce((sub, item, iIdx) => {
        if (item?.optional) return sub;
        const key = `${sIdx}_${iIdx}`;
        const calc = itemCalculations[key] || { slt: 0 };
        return sub + Number(calc.slt || 0);
      }, 0);
    }, 0);
    
    const deploymentCount = Object.keys(templateUsages).length;
    
    // If no usages, return just SLT
    if (deploymentCount === 0) {
      return totalSLT;
    }
    
    // Calculate usage totals
    const usageTotals = Object.keys(templateUsages).reduce((acc, usageKey) => {
      const total = sectionBlocks.reduce((sum, section, sIdx) => {
        return sum + (section.items || []).reduce((sub, item, iIdx) => {
          if (item?.optional) return sub;
          const key = `${sIdx}_${iIdx}`;
          const calc = itemCalculations[key] || { usages: {} };
          return sub + Number(calc.usages?.[usageKey] || 0);
        }, 0);
      }, 0);
      acc[usageKey] = total;
      return acc;
    }, {});

    const totalUsageSum = Object.values(usageTotals).reduce((a, b) => a + Number(b || 0), 0);
    
    // Average = (SLT + all usages) / (1 + number of usages)
    return (totalSLT + totalUsageSum) / (1 + deploymentCount);
  }, [sectionBlocks, itemCalculations, templateUsages]);

  // Contractor modal functions
  const openContractorModal = (sectionIndex, itemIndex) => {
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    if (item?.contractor_items) {
      setContractorItems(item.contractor_items);
      setSelectedContractor(item.contractor || '');
      setContractorSearch(item.contractor || '');
    } else {
      setContractorItems([]);
      setSelectedContractor('');
      setContractorSearch('');
    }
    setContractorModal({ isOpen: true, sectionIndex, itemIndex });
  };

  // Search contractors
  const searchContractors = async (query) => {
    if (!query || query.length < 2) {
      setContractorSuggestions([]);
      setShowContractorSuggestions(false);
      return;
    }
    
    try {
      const response = await api.get(`/api/users/suppliers?q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setContractorSuggestions(response.data.data.suppliers || []);
        setShowContractorSuggestions(true);
      }
    } catch (error) {
      console.error('Error searching contractors:', error);
      setContractorSuggestions([]);
    }
  };

  const selectContractor = (contractor) => {
    setSelectedContractor({ id: contractor.id, name: contractor.name });
    setContractorSearch(contractor.name);
    setShowContractorSuggestions(false);
  };

  const addContractorItem = () => {
    setContractorItems(prev => [...prev, { name: '', quantity: 1, unit_price: 0, total_price: 0 }]);
  };

  const updateContractorItem = (index, field, value) => {
    setContractorItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Calculate total price
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? value : updated[index].quantity;
        const price = field === 'unit_price' ? value : updated[index].unit_price;
        updated[index].total_price = Number(qty) * Number(price);
      }
      
      return updated;
    });
  };

  const removeContractorItem = (index) => {
    setContractorItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveContractorData = () => {
    const { sectionIndex, itemIndex } = contractorModal;
    const totalAmount = contractorItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    
    // Update section blocks with contractor data
    setSectionBlocks(prev => prev.map((section, i) => {
      if (i !== sectionIndex) return section;
      return {
        ...section,
        items: section.items.map((item, j) => {
          if (j !== itemIndex) return item;
          const qty = Number(item.quantity || 1);
          const perUnit = qty > 0 ? Number(totalAmount) / qty : 0;
          return {
            ...item,
            contractor_id: selectedContractor?.id || null,
            contractor: selectedContractor?.name || '',
            contractor_items: [...contractorItems],
            contractor_total: totalAmount,
            unit_price: perUnit
          };
        })
      };
    }));
    
    // Close modal and clean up states
    setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null });
    setContractorSearch('');
    setContractorSuggestions([]);
    setShowContractorSuggestions(false);
    toast({ title: t('common.success') || 'Success', description: 'Contractor data saved successfully', status: 'success' });
  };

  // Save new supplier
  const saveNewSupplier = async () => {
    if (!newSupplierForm.name) {
      toast({ title: t('common.error'), description: 'Name is required', status: 'error' });
      return;
    }
    
    try {
      const response = await api.post('/api/users/suppliers', newSupplierForm);
      if (response.data.success) {
        // Backend returns {success: true, data: {id: X}}
        const supplierId = response.data.data.id;
        const newSupplier = {
          id: supplierId,
          name: newSupplierForm.name,
          email: newSupplierForm.email,
          phone: newSupplierForm.phone
        };
        // Set as selected contractor
        setSelectedContractor(newSupplier);
        setContractorSearch(newSupplier.name);
        // Close new supplier modal
        setNewSupplierModal(false);
        setNewSupplierForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          category: '',
          rating: '',
          notes: ''
        });
        toast({ title: t('common.success'), description: 'Supplier created successfully', status: 'success' });
      }
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast({ title: t('common.error'), description: error.response?.data?.error || 'Failed to create supplier', status: 'error' });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={6}>
        <Heading size="lg">{proposalType === 'proforma' ? (t('proposals.new_proforma') || 'New Proforma') : (t('proposals.new_offer') || 'New Offer')}</Heading>
        <HStack spacing={4}>
          {/* Proposal Type Toggle */}
          <HStack spacing={2}>
            <Button 
              size="sm" 
              variant={proposalType === 'offer' ? 'solid' : 'outline'} 
              colorScheme={proposalType === 'offer' ? 'blue' : 'gray'}
              onClick={() => setProposalType('offer')}
            >
              {t('proposals.type.offer') || 'Offer'}
            </Button>
            <Button 
              size="sm" 
              variant={proposalType === 'proforma' ? 'solid' : 'outline'} 
              colorScheme={proposalType === 'proforma' ? 'blue' : 'gray'}
              onClick={() => setProposalType('proforma')}
            >
              {t('proposals.type.proforma') || 'Proforma'}
            </Button>
          </HStack>
          {/* Show Detailed Pricing Toggle */}
          <Switch
            isChecked={showDetailedPricing}
            onChange={(e) => setShowDetailedPricing(e.target.checked)}
            size="md"
          />
          <Text fontSize="sm">{showDetailedPricing ? (t('proposals.show_detailed_pricing') || 'Show detailed pricing') : (t('proposals.hide_detailed_pricing') || 'Hide detailed pricing')}</Text>
        </HStack>
      </HStack>

      <Card bg={bgColor} border="1px" borderColor={borderColor} boxShadow="lg" borderRadius="xl" w="100%">
        <CardBody p={1} w="100%">
          <VStack spacing={1}  align="stretch" w="100%" pt={4} px={2}>
            {/* Row 1: Client, Code+Version, Template */}
            <HStack align="flex-end" spacing={6} flexWrap="wrap" mb={2} p={2}>
              <Box minW="420px" flex={1}>
                <InlineField label={t('clients.title') || 'Clients'} required minLabelW="140px">
                  <SearchableSelect
                    value={selection.client_id}
                    returnObject
                    onChange={(opt) => setSelection({ ...selection, client_id: opt?.id || opt, client_name: opt?.name || '', client_company: opt?.company || '' })}
                    placeholder={t('common.select') || 'Select'}
                    searchPlaceholder={t('common.search') || 'Search'}
                    fetchUrl={'/api/users/clients?q='}
                    valueKey="id"
                    labelKey="company"
                    fallbackLabelKey="name"
                    minChars={0}
                    size="sm"
                    isRequired
                  />
                </InlineField>
              </Box>
              <Box minW="240px">
                <InlineField label={t('proposals.version_code') || 'VERSION CODE'} minLabelW="140px">
                  <Input size="sm" value={versionNote} onChange={(e)=> setVersionNote(e.target.value)} placeholder={t('proposals.version_code')||'VERSION CODE'} />
                </InlineField>
              </Box>
              <Box minW="160px">
                <InlineField label={t('proposals.version') || 'Version'} minLabelW="140px">
                  <Input size="sm" value={version} onChange={(e) => setVersion(e.target.value)} />
                </InlineField>
              </Box>
              <Box minW="420px" flex={1}>
                <InlineField label={t('proposals.select_template') || 'Select Template'} required minLabelW="140px">
                  <Select size="sm" value={selection.template_id} onChange={(e) => {
                    const id = e.target.value;
                    const tpl = templates?.find(t => String(t.id) === String(id));
                    setSelection({ ...selection, template_id: id, template_name: tpl?.name || '' });
                    try {
                      const templateData = tpl?.json || {};
                      const rawSections = templateData.sections || [];
                      const rawUsages = templateData.usages || {};
                      setTemplateUsages(rawUsages);
                      // notes'i meta'ya yükle
                      if (typeof templateData.notes === 'string') {
                        setMeta(prev => ({ ...prev, notes: templateData.notes }));
                      }
                      if (Array.isArray(rawSections) && rawSections.length > 0) {
                        const mapped = rawSections.map((s) => {
                          // Support both old format (name) and new format (name_en, name_de)
                          const nameEn = s.name_en || s.name || s.section_name || s.title || 'Section';
                          const nameDe = s.name_de || s.name || s.section_name || s.title || 'Section';
                          const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
                          return {
                            section_id: s.section_id || s.id || null,
                            section_name: sectionName,
                            name_en: nameEn,
                            name_de: nameDe,
                            margin: Number(s.margin || 1.0),
                            items: []
                          };
                        });
                        setSectionBlocks(mapped);
                      } else {
                        setSectionBlocks([]);
                      }
                    } catch (_) {
                      setSectionBlocks([]);
                      setTemplateUsages({});
                    }
                    if (id) { loadFormulaRules(id); }
                  }}>
                    <option value="">{t('common.select') || 'Select'}</option>
                    {templates?.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </Select>
                </InlineField>
              </Box>
            </HStack>

            {/* Row 2: Proposal Title manual - "Offers" input */}
            {isReady && (
              <Box p={2} mb={2}>
                <InlineField label={t('proposals.title') || 'Proposals'} minLabelW="140px">
                  <Input value={title} onChange={(e)=> setTitle(e.target.value)} placeholder={t('proposals.title')||'Title'} />
                </InlineField>
              </Box>
            )}

          {/* Meta - düz görünüm, çerçevesiz */}
          <VStack align="stretch" spacing={3} p={2}>
            <InlineField label={t('proposals.meta.project') || 'Project'} minLabelW="140px">
              <Input value={meta.project} onChange={(e)=> setMeta({...meta, project: e.target.value})} />
            </InlineField>
            <InlineField label={t('proposals.meta.subject') || 'Subject'} minLabelW="140px">
              <Input value={meta.subject} onChange={(e)=> setMeta({...meta, subject: e.target.value})} />
            </InlineField>
            <InlineField label={t('proposals.meta.in_charge') || 'In charge'} minLabelW="140px">
              <VStack align="stretch" spacing={2}>
                <Select 
                  value="" 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !meta.in_charge.includes(value)) {
                      setMeta({...meta, in_charge: [...meta.in_charge, value]});
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">{t('common.select')||'Select'}</option>
                  {userNames?.filter(u => !meta.in_charge.includes(u.full_name)).map(u=> (
                    <option key={u.id} value={u.full_name}>{u.full_name}</option>
                  ))}
                </Select>
                {meta.in_charge.length > 0 && (
                  <HStack spacing={2} flexWrap="wrap">
                    {meta.in_charge.map((name, idx) => (
                      <Tag key={idx} size="md" colorScheme="blue">
                        <TagLabel>{name}</TagLabel>
                        <TagCloseButton onClick={() => {
                          setMeta({...meta, in_charge: meta.in_charge.filter((_, i) => i !== idx)});
                        }} />
                      </Tag>
                    ))}
                  </HStack>
                )}
              </VStack>
            </InlineField>
            <InlineField label="Notes" minLabelW="140px">
              <Textarea value={meta.notes} onChange={(e)=> setMeta({...meta, notes: e.target.value})} rows={3} />
            </InlineField>
          </VStack>

            {/* Footer paneli en alta taşındı: Global Totals'tan sonra render edilecek */}

            {/* Section seçimi ve blok ekleme */}
            {isReady && (
              <Box p={2} mb={2}>
                <InlineField label={t('proposals.select_section') || 'Select Section'} minLabelW="140px">
                  <HStack w="100%">
                    <Select 
                      key={`section-select-${currentLanguage}`}
                      size="sm" 
                      value={selectedSectionId} 
                      onChange={(e) => setSelectedSectionId(e.target.value)}
                    >
                      <option value="">{t('common.select') || 'Select'}</option>
                      {sections?.map(sec => {
                        const sectionName = currentLanguage === 'de' 
                          ? (sec.name_de || sec.name_en || 'Section')
                          : (sec.name_en || sec.name_de || 'Section');
                        return (
                          <option key={sec.id} value={sec.id}>{sectionName}</option>
                        );
                      })}
                    </Select>
                    <Button onClick={addSectionBlock} isDisabled={!selectedSectionId} size="sm" height="32px">{t('common.add') || 'Add'}</Button>
                  </HStack>
                </InlineField>
              </Box>
            )}

            {/* Eklenen section blokları */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
              <SortableContext items={sectionBlocks.map((_, i) => `section-${i}`)} strategy={verticalListSortingStrategy}>
                {sectionBlocks.map((blk, idx) => (
                  <SortableSection key={`section-${idx}`} id={`section-${idx}`}>
                    {({ dragListeners }) => (
                      <Card bg="white" border="1px" borderColor="gray.200" boxShadow="lg" borderRadius="xl" overflow="hidden" w="100%">
                <Box 
                  bg="linear-gradient(135deg,rgb(0, 0, 0) 0%,rgb(102, 102, 102) 100%)" 
                  py={1}
                  px={2}
                  position="relative"
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(255,255,255,0.1)"
                    backdropFilter="blur(10px)"
                  />
                  <Flex justify="space-between" align="center" position="relative" zIndex={1}>
                    <HStack spacing={6}>
                      <Text 
                        fontWeight="bold" 
                        fontSize="xl" 
                        color="white"
                        textShadow="0 2px 4px rgba(0,0,0,0.3)"
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                        {...dragListeners}
                      >
                        {blk.section_name}
                      </Text>
                      <HStack spacing={3} px={4} py={1} borderRadius="full" backdropFilter="blur(10px)">
                        <Text fontSize="sm" color="white" fontWeight="medium">
                          Margin:
                        </Text>
                        <Input
                          size="xs"
                          type="number"
                          step="1"
                          min="0"
                          maxW="60px"
                          value={blk.margin === null || blk.margin === undefined ? '' : blk.margin}
                          onChange={(e) => setSectionBlocks(prev => prev.map((b, i) => 
                            i === idx ? { ...b, margin: e.target.value === '' ? null : Number(e.target.value) } : b
                          ))}
                          placeholder="0"
                          bg="white"
                          border="none"
                          _focus={{ boxShadow: "0 0 0 2px rgba(255,255,255,0.5)" }}

                        />
                        <Text fontSize="sm" color="white" fontWeight="medium">%</Text>
                      </HStack>
                    </HStack>
                    <IconButton
                      aria-label="delete section"
                      size="sm"
                      variant="ghost"
                      colorScheme="whiteAlpha"
                      color="white"
                      _hover={{ bg: "rgba(255,255,255,0.2)" }}
                      icon={<FiX />}
                      onClick={() => {
                        if (window.confirm(t('messages.confirm_delete'))) {
                          setSectionBlocks(prev => prev.filter((_, i) => i !== idx));
                        }
                      }}
                    />
                  </Flex>
                </Box>
                <CardBody bg="white" p={6} w="100%" overflow="visible">
                  <VStack align="stretch" spacing={4} gap={0} w="100%" overflow="visible">
                    <Box bg="blue.50" p={4} borderRadius="lg" border="2px dashed" borderColor="blue.200" overflow="visible">
                      <SearchableSelect
                        label={
                          <HStack>
                            <Text fontWeight="bold" color="blue.700" fontSize="md">
                              🔍 {t('inventory.select_item') || 'Select Product'}
                            </Text>
                            <Text fontSize="sm" color="blue.600">
                              - {t('inventory.search_hint') || 'Start typing to search products'}
                            </Text>
                          </HStack>
                        }
                        value={''}
                        returnObject
                        clearOnSelect
                        onChange={(opt) => {
                          if (!opt || typeof opt !== 'object') return; // yazarken tetiklenen null değerleri yoksay
                          const itemName = (currentLanguage === 'de') ? (opt?.name_de || opt?.name || String(opt)) : (opt?.name_en || opt?.name || String(opt));
                          const itemId = opt?.id || null;
                          const itemType = (opt?.__source && String(opt.__source).includes('/api/services')) ? 'service' : 'product';
                          const unitPrice = opt?.unit_price || 0;
                          // units: inventory item -> provided, service -> no uom, keep undefined
                          const abbr = currentLanguage === 'de' ? (opt?.uom_abbr_de || opt?.uom) : (opt?.uom_abbr_en || opt?.uom);
                          setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                            ...b,
                            items: [...b.items, { 
                              item_id: itemId, 
                              name: itemName, 
                              name_en: opt?.name_en || opt?.name || '',
                              name_de: opt?.name_de || opt?.name || '',
                              quantity: 1, 
                              unit_price: unitPrice, 
                              uom_abbr: abbr, 
                              item_type: itemType,
                              margin: null // Yeni ürün için margin boş
                            }]
                          } : b));
                        }}
                        placeholder={t('inventory.search_placeholder') || 'Type product name, category or SKU...'}
                        searchPlaceholder={t('inventory.search_placeholder') || 'Type product name, category or SKU...'}
                        fetchUrls={['/api/inventory?q=', '/api/services?q=']}
                        allowCreate
                        onCreateOption={(label) => {
                          setCreateItemModal({ isOpen: true, label, sectionIndex: idx });
                        }}
                        valueKey="id"
                        labelKey={currentLanguage === 'de' ? 'name_de' : 'name_en'}
                        minChars={1}
                        size="md"
                      />
                    </Box>
                    {/* Tablo Başlığı */}
                    <Flex fontWeight="700" fontSize="sm" textAlign="center" gap={0} mt={4} w="100%" sx={{
                      '& > *': {
                        height: '60px !important',
                        minHeight: '60px !important',
                        maxHeight: '60px !important'
                      }
                    }}>
                      {/* Ana Bilgiler - Koyu Yeşil */}
                      <Box 
                        bg="green.600" 
                        p={3} 
                        display="flex" 
                        alignItems="center" 
                        h="60px" 
                        flex="1"
                        gap={0}
                      >
                        <Box w="60px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.pos') || 'Pos.'}</Text>
                        </Box>
                        <Box w="65px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.qty') || 'Quantity'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.unit') || 'Unit'}</Text>
                        </Box>
                        <Box w="240px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.description') || 'Description'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.sppu') || 'SPPU'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.slt') || 'SLT'}</Text>
                        </Box>
                      </Box>
                      
                      {/* Dynamic Usage Columns - Koyu Mavi */}
                      {Object.entries(templateUsages).map(([usageKey, usage]) => (
                        <Box 
                          key={usageKey} 
                          w="140px" 
                          textAlign="center" 
                          bg="blue.700" 
                          p={3} 
                          h="60px"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="sm" fontWeight="extrabold" color="white">
                            {currentLanguage === 'de' ? usage['lang.de'] : usage['lang.en']}
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.800" mt={1}>
                            ×{usage.ratio}
                          </Text>
                        </Box>
                      ))}
                      
                      {/* Diğer Kolonlar - Koyu Gri */}
                      <Box 
                        w="140px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.contractor') || 'Contractor'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.final_price') || 'Final Price'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.margin') || 'Margin'}</Text>
                      </Box>
                      <Box 
                        w="160px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.unit_price_x_amount') || 'Unit x Qty'}</Text>
                      </Box>
                      <Box 
                        w="140px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.unit_price') || 'UP'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={1}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.notes') || 'Notes'}</Text>
                      </Box>
                      <Box w="36px"></Box>
                    </Flex>
                    {/* Satırlar */}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd(idx)}>
                      <SortableContext items={blk.items.map((_, i) => `item-${idx}-${i}`)} strategy={verticalListSortingStrategy}>
                        {blk.items.map((it, jdx) => {
                          // Get calculations from state
                          const calculationKey = `${idx}_${jdx}`;
                          const calculations = itemCalculations[calculationKey] || {
                            sppu: 0,
                            slt: 0,
                            final_price: 0,
                            unit_price_x_amount: 0,
                            usages: {}
                          };
                          
                          return (
                            <SortableItem key={`item-${idx}-${jdx}`} id={`item-${idx}-${jdx}`} isOptional={it?.optional}>
                              {({ dragListeners }) => (
                                <Flex align="stretch" gap={0} mb={1} w="100%" minH="60px" onContextMenu={(e)=>{
                          e.preventDefault();
                          setRowContextMenu({ open: true, x: e.clientX, y: e.clientY, sectionIndex: idx, itemIndex: jdx });
                        }}>
                        <Box 
                          bg={(it?.optional ? "black" : (jdx % 2 === 0 ? "green.50" : "green.100"))} 
                          color={it?.optional ? "white" : undefined}
                          p={3} 
                          display="flex" 
                          alignItems="center" 
                          minH="48px" 
                          flex="1"
                          gap={0}
                        >
                          <Box w="60px" minH="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center" cursor="grab" _active={{ cursor: 'grabbing' }} {...dragListeners}>
                            <Text fontSize="sm" fontWeight="medium" color={it?.optional ? "white" : "green.800"}>
                              {String(jdx + 1).padStart(2, '0')}
                            </Text>
                          </Box>
                          <Box w="65px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                        <Input 
                          type="number" 
                          min={1} 
                          value={it.quantity === 0 ? '' : it.quantity}
                          onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                            ...b,
                            items: b.items.map((x,k)=> k===jdx ? { ...x, quantity: e.target.value === '' ? 0 : Number(e.target.value) } : x)
                          } : b))}
                          size="sm"
                          bg="transparent"
                          border="none"
                          _focus={{ boxShadow: "none" }}
                          w="100%"
                          textAlign="center"
                          color={it?.optional ? "white" : undefined}
                        />
                          </Box>
                          <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                            {editingUnits[`${idx}_${jdx}`] ? (
                              <HStack spacing={1}>
                                <Select
                                  value={it.uom_abbr || ''}
                                  onChange={(e) => {
                                    setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                                      ...b,
                                      items: b.items.map((x, k) => k === jdx ? { ...x, uom_abbr: e.target.value } : x)
                                    } : b));
                                    setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }));
                                  }}
                                  size="sm"
                                  autoFocus
                                >
                                  <option value="">-</option>
                                  {units?.map(unit => (
                                    <option key={unit.id} value={currentLanguage === 'de' ? unit.abbreviation_de : unit.abbreviation_en}>
                                      {currentLanguage === 'de' ? unit.abbreviation_de : unit.abbreviation_en}
                                    </option>
                                  ))}
                                </Select>
                                <Input
                                  value={it.uom_abbr || ''}
                                  onChange={(e) => {
                                    console.log('Input onChange:', e.target.value, 'for item:', it);
                                    setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                                      ...b,
                                      items: b.items.map((x, k) => k === jdx ? { ...x, uom_abbr: e.target.value } : x)
                                    } : b));
                                  }}
                                  onBlur={() => setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }))}
                                  onKeyPress={(e) => e.key === 'Enter' && setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }))}
                                  size="sm"
                                  placeholder="Manuel giriş"
                                  minW="60px"
                                  w="60px"
                                  isReadOnly={false}
                                />
                              </HStack>
                            ) : (
                              <Box
                                cursor="pointer"
                                onClick={() => setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: true }))}
                                _hover={{ bg: it?.optional ? 'gray.800' : 'gray.100' }}
                                p={1}
                                color={it?.optional ? "white" : undefined}
                              >
                                {it.uom_abbr || '-'}
                              </Box>
                            )}
                          </Box>
                          <Box w="240px" minH="60px" display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center" p={2} position="relative" role="group" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {it?.optional && (
                              <Text fontSize="xs" fontWeight="extrabold" color="yellow.300" letterSpacing="0.06em" mb={1}>
                                {t('proposal.item.optional_label')?.toLocaleUpperCase('en-US') || 'OPTIONAL'}
                              </Text>
                            )}
                            {it?.product_note && (
                              <Text fontSize="xs" fontWeight="bold" color="blue.300" mb={1} cursor="pointer" onClick={() => setNoteModal({ isOpen: true, type: 'product_note', sectionIndex: idx, itemIndex: jdx, value: it.product_note })}>📝 {it.product_note}</Text>
                            )}
                            <Text textAlign="left" fontSize="sm">{currentLanguage === 'de' ? (it.name_de || it.name || it.name_en) : (it.name_en || it.name || it.name_de)}</Text>
                            <IconButton aria-label="add sub description" size="xs" variant="ghost" icon={<FiPlus />} onClick={() => openSubDesc(idx, jdx)} position="absolute" top="4px" right="4px" opacity={0} _groupHover={{ opacity: 1 }} />
                            {Array.isArray(it.sub_descriptions) && it.sub_descriptions.length > 0 && (
                              <HStack mt={2} spacing={1} wrap="wrap">
                                {it.sub_descriptions.map((sd, i2) => (
                                  <Tag key={i2} size="sm" colorScheme={String(sd).startsWith('**') ? 'blue' : 'gray'} variant="subtle" position="relative" role="group"
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const isBold = String(sd).startsWith('**');
                                      const cleanText = String(sd).replace(/^\*\*/, '');
                                      setSectionBlocks(prev => prev.map((b, i) => i === idx ? ({
                                        ...b,
                                        items: b.items.map((x, k) => k === jdx ? ({
                                          ...x,
                                          sub_descriptions: (x.sub_descriptions || []).map((v, si) => si === i2 ? (isBold ? cleanText : '**' + cleanText) : v)
                                        }) : x)
                                      }) : b));
                                    }}
                                  >
                                    <TagLabel fontWeight={String(sd).startsWith('**') ? 'bold' : 'normal'}>+ {String(sd).replace(/^\*\*/, '')}</TagLabel>
                                    <TagCloseButton
                                      aria-label="remove sub"
                                      color="red.500"
                                      _hover={{ bg: 'red.50', color: 'red.600' }}
                                      opacity={0}
                                      _groupHover={{ opacity: 1 }}
                                      onClick={() => {
                                        setSectionBlocks(prev => prev.map((b, i) => i === idx ? ({
                                          ...b,
                                          items: b.items.map((x, k) => k === jdx ? ({
                                            ...x,
                                            sub_descriptions: (x.sub_descriptions || []).filter((v, si) => !(si === i2 && v === sd))
                                          }) : x)
                                        }) : b));
                                      }}
                                    />
                                  </Tag>
                                ))}
                              </HStack>
                            )}
                          </Box>
      <Modal isOpen={subDescModal.isOpen} onClose={() => setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null })} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr="72px">
            <HStack justify="space-between">
              <Text>{t('table.description') || 'Description'} – Sub items</Text>
              <Button size="sm" colorScheme="blue" mr={2} onClick={() => {
                const { sectionIndex, itemIndex } = subDescModal;
                const subList = (subDescOptions || []).map(x => x.sub_description).filter(Boolean);
                if (subList.length === 0) return;
                setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                  ...b,
                  items: b.items.map((it, k) => k === itemIndex ? ({
                    ...it,
                    sub_descriptions: Array.isArray(it.sub_descriptions) ? Array.from(new Set([...it.sub_descriptions, ...subList])) : Array.from(new Set(subList))
                  }) : it)
                }) : b));
                toast({ title: t('common.success'), status: 'success' });
                setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null });
              }}>
                {t('common.add_all') || 'Add all'}
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Input placeholder={t('common.search') || 'Search'} value={subDescQuery} onChange={(e)=>{ setSubDescQuery(e.target.value); const item = sectionBlocks[subDescModal.sectionIndex]?.items[subDescModal.itemIndex]; loadSubDescriptions(item); }} />
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" maxH="260px" overflowY="auto" p={2}>
                {subDescLoading ? (
                  <Text fontSize="sm" color="gray.500">Loading...</Text>
                ) : (
                  (subDescOptions || []).map(opt => (
                    <HStack key={opt.id} p={2} _hover={{ bg:'gray.50' }} justify="space-between">
                      <Text fontSize="sm" flex={1}>+ {opt.sub_description}</Text>
                      <HStack spacing={2}>
                        <Button size="xs" onClick={() => {
                          const { sectionIndex, itemIndex } = subDescModal;
                          const val = opt.sub_description || '';
                          if (!val) return;
                          setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                            ...b,
                            items: b.items.map((it, k) => k === itemIndex ? ({
                              ...it,
                              sub_descriptions: Array.isArray(it.sub_descriptions) ? Array.from(new Set([...it.sub_descriptions, val])) : [val]
                            }) : it)
                          }) : b));
                        }}>
                          {t('common.add') || 'Add'}
                        </Button>
                        <IconButton 
                          aria-label="delete" 
                          size="sm" 
                          variant="ghost" 
                          colorScheme="red" 
                          icon={<FiTrash2 />} 
                          onClick={() => removeSubDescription(opt.id)} 
                        />
                      </HStack>
                    </HStack>
                  ))
                )}
              </Box>
              <Input placeholder={t('common.add') || 'Add sub description'} value={subDescInput} onChange={(e)=> setSubDescInput(e.target.value)} />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={()=> setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null })}>{t('common.close') || 'Close'}</Button>
              <Button colorScheme="blue" onClick={addSubDescription} isDisabled={!subDescInput.trim()}> {t('common.save') || 'Save'} </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
                          <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                            <Text fontSize="sm" fontWeight="medium" color={it?.optional ? 'white' : undefined} textAlign="center">
                              {formatCurrency(calculations.sppu || 0)}
                            </Text>
                          </Box>
                          <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                            <Text fontSize="sm" fontWeight="medium" color={it?.optional ? 'white' : undefined} textAlign="center">
                              {formatCurrency(calculations.slt || 0)}
                            </Text>
                          </Box>
                        </Box>
                        {/* Dynamic Usage Columns */}
                        {Object.entries(templateUsages).map(([usageKey, usage]) => {
                          const usageValue = calculations.usages[usageKey] || 0;
                          const itemUsageKey = `${idx}_${jdx}_${usageKey}`;
                          const isEditingUsage = editingUsage === itemUsageKey;
                          const customRatio = itemUsages[itemUsageKey]?.ratio || usage.ratio;
                          
                          return (
                            <Box key={usageKey} w="140px" minH="60px" textAlign="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "blue.50" : "blue.100")}  borderColor="blue.200" color={it?.optional ? "white" : undefined} display="flex" alignItems="center" justifyContent="center">
                          {isEditingUsage ? (
                                <Box
                                  bg={jdx % 2 === 0 ? "blue.50" : "blue.100"}
                                  minH="60px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  w="100%"
                                >
                                  <Input
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={customRatio === 0 ? '' : customRatio}
                                    onChange={(e) => setItemUsages(prev => ({
                                      ...prev,
                                      [itemUsageKey]: { ...prev[itemUsageKey], ratio: e.target.value === '' ? 0 : Number(e.target.value) }
                                    }))}
                                    onBlur={() => setEditingUsage(null)}
                                    onKeyPress={(e) => e.key === 'Enter' && setEditingUsage(null)}
                                    autoFocus
                                    maxW="70px"
                                    bg="white"
                                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                    textAlign="center"
                                  />
                                </Box>
                              ) : (
                                <Box
                                  cursor="pointer"
                                  onClick={() => setEditingUsage(itemUsageKey)}
                                  _hover={{ bg: it?.optional ? 'gray.900' : 'blue.50', transform: "scale(1.02)" }}
                                  borderRadius="md"
                                  minH="60px"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                  transition="all 0.2s"
                                  position="relative"
                                  w="100%"
                                >
                                    <Text fontSize="sm" fontWeight="medium" color={it?.optional ? "white" : "blue.700"}>
                                      {formatCurrency(usageValue || 0)}
                                    </Text>
                                  {/* Only show ratio if it's different from template default */}
                                  {customRatio !== usage.ratio && (
                                    <Box
                                      position="absolute"
                                      bottom="4px"
                                      left="50%"
                                      transform="translateX(-50%)"
                                      bg="blue.100"
                                      borderRadius="full"
                                      px={2}
                                      py={0.5}
                                      display="inline-block"
                                      boxShadow="sm"
                                      zIndex={1}
                                    >
                                      <Text fontSize="9px" color={it?.optional ? "white" : "blue.700"} fontWeight="bold">
                                        ×{customRatio}
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                        <Box w="140px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? 'black' : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? 'white' : undefined}>
                          <Box
                            cursor="pointer"
                            onClick={() => openContractorModal(idx, jdx)}
                            _hover={{ bg: it?.optional ? 'gray.900' : 'gray.200' }}
                            p={2}
                            w="100%"
                            h="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="sm" color={it?.optional ? 'white' : 'gray.700'}>
                              {it.contractor || '-'}
                            </Text>
                            {it.contractor_items && it.contractor_items.length > 0 && (
                              <Text fontSize="xs" color={it?.optional ? 'green.300' : 'green.600'} ml={1}>
                                ({it.contractor_items.length})
                              </Text>
                            )}
                          </Box>
                        </Box>
                        <Box w="120px" minH="60px" display="flex" flexDirection="column" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                          <Input
                            value={formatCurrency(calculations.final_price || 0)}
                            isReadOnly
                            size="sm"
                            bg="transparent"
                            color={it?.optional ? 'white' : undefined}
                            border="none"
                            w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                          />
                          {it?.hide_price_note && (
                            <Text fontSize="2xs" color="red.400" textAlign="center" px={1} lineHeight="1.2" cursor="pointer" onClick={() => setNoteModal({ isOpen: true, type: 'hide_price_note', sectionIndex: idx, itemIndex: jdx, value: it.hide_price_note })}>⚠ {it.hide_price_note}</Text>
                          )}
                        </Box>
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? 'black' : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? 'white' : undefined}>
                          <Input 
                            type="number" 
                            step="1" 
                            placeholder={(blk.margin !== null && blk.margin !== undefined) ? String(blk.margin) : "0"}
                            value={(() => {
                              const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                              return marginValue === 0 ? '0' : (marginValue !== null && marginValue !== undefined ? marginValue : '');
                            })()}
                            onFocus={(e) => {
                              const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                              if (marginValue === 0) {
                                e.target.select();
                                setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                  ...b,
                                  items: b.items.map((x,k)=> k===jdx ? { ...x, margin: null } : x)
                                } : b));
                              }
                            }}
                            onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                              ...b,
                              items: b.items.map((x,k)=> k===jdx ? { ...x, margin: e.target.value === '' ? null : Number(e.target.value) } : x)
                            } : b))}
                            size="sm"
                            bg="transparent"
                            color={(() => {
                              const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                              if (marginValue === 0) return '#ff0000';
                              return it?.optional ? 'white' : undefined;
                            })()}
                            fontWeight={(() => {
                              const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                              return marginValue === 0 ? 700 : undefined;
                            })()}
                            border="none"
                            w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                            _placeholder={{ color: 'gray.400' }}
                          />
                        </Box>
                        <Box w="160px" minH="60px" textAlign="center" fontSize="sm" fontWeight="medium" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                          {formatCurrency(it.contractor_total ? it.contractor_total : (calculations.unit_price_x_amount || 0))}
                        </Box>
                        <Box w="140px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                        <Input 
                          type="number" 
                          min={0} 
                          value={it.unit_price === 0 ? '' : it.unit_price}
                          onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                            ...b,
                            items: b.items.map((x,k)=> k===jdx ? { ...x, unit_price: e.target.value === '' ? 0 : Number(e.target.value) } : x)
                          } : b))}
                          size="sm"
                          bg="transparent"
                          color={it?.optional ? 'white' : undefined}
                          border="none"
                          w="100%"
                          textAlign="center"
                          _focus={{ boxShadow: "none" }}
                        />
                        </Box>
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                          <Input 
                            value={it.notes || ''} 
                            placeholder={t('common.add_note') || 'Add note...'}
                            onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                              ...b,
                              items: b.items.map((x,k)=> k===jdx ? { ...x, notes: e.target.value } : x)
                            } : b))}
                            size="sm"
                            bg="transparent"
                            color={it?.optional ? 'white' : undefined}
                            _placeholder={it?.optional ? { color: 'gray.300' } : undefined}
                            border="none"
                            w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                          />
                        </Box>
  
                        <Box w="36px" textAlign="right">
                          <IconButton aria-label="delete row" size="xs" variant="ghost" colorScheme="red" color="red.500" _hover={{ color: 'red.600' }} icon={<FiX />} onClick={() => {
                            if (window.confirm(t('messages.confirm_delete'))) {
                              setSectionBlocks(prev => prev.map((b,i)=> i===idx ? {
                                ...b,
                                items: b.items.filter((_,k)=> k!==jdx)
                              } : b));
                            }
                          }} />
                        </Box>
                              </Flex>
                              )}
                            </SortableItem>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                    {/* Section totals - exclude optional items from totals */}
                    <Flex align="stretch" mb={1} w="100%" minH="60px" gap={0}>
                      {/* Placeholders until SLT column */}
                      <Box flex="1" p={3} display="flex" gap={0} alignItems="center" bg="green.50">
                        <Box w="60px" minH="60px" bg="green.50" />
                        <Box w="65px" minH="60px" bg="green.50" />
                        <Box w="120px" minH="60px" bg="green.50" />
                        <Box w="240px" minH="60px" bg="green.50" /> {/* Description column - 240px */}
                        <Box w="120px" minH="60px" bg="green.50" /> {/* SPPU column */}
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg="green.50">
                          <Text fontWeight="extrabold" textAlign="center" w="100%" fontSize="sm">
                            {formatCurrency((blk.items || []).reduce((sum, item, itemIdx) => {
                              if (item?.optional) return sum;
                              const key = `${idx}_${itemIdx}`;
                              const calc = itemCalculations[key] || { slt: 0 };
                              return sum + Number(calc.slt || 0);
                            }, 0))}
                          </Text>
                        </Box>
                      </Box>

                      {Object.entries(templateUsages).map(([usageKey]) => {
                        const usageTotal = (blk.items || []).reduce((sum, item, itemIdx) => {
                          if (item?.optional) return sum;
                          const key = `${idx}_${itemIdx}`;
                          const calc = itemCalculations[key] || { usages: {} };
                          return sum + Number(calc.usages?.[usageKey] || 0);
                        }, 0);
                        return (
                          <Box key={usageKey} w="140px" minH="60px" p={3} display="flex" alignItems="center" justifyContent="center" bg="blue.50">
                            <Text fontWeight="extrabold" textAlign="center" w="100%" fontSize="sm">{formatCurrency(usageTotal)}</Text>
                          </Box>
                        );
                      })}

                      {/* Placeholders for remaining columns */}
                      <Box w="140px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="160px" minH="60px" p={3} bg="gray.50" />
                      <Box w="140px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="36px" minH="60px" bg="transparent" />
                    </Flex>
          </VStack>
        </CardBody>
                    </Card>
                    )}
                  </SortableSection>
                ))}
              </SortableContext>
            </DndContext>
          </VStack>
        </CardBody>
      </Card>
      {/* Global totals summary below all sections */}
      {sectionBlocks.length > 0 && (
        <Card bg={bgColor} border="1px" borderColor={borderColor} boxShadow="md" borderRadius="xl" mt={4}>
          <CardBody>
            {(() => {
              const totalSLT = sectionBlocks.reduce((sum, section, sIdx) => {
                return sum + (section.items || []).reduce((sub, item, iIdx) => {
                  if (item?.optional) return sub;
                  const key = `${sIdx}_${iIdx}`;
                  const calc = itemCalculations[key] || { slt: 0 };
                  return sub + Number(calc.slt || 0);
                }, 0);
              }, 0);

              const usageTotals = Object.keys(templateUsages).reduce((acc, usageKey) => {
                const total = sectionBlocks.reduce((sum, section, sIdx) => {
                  return sum + (section.items || []).reduce((sub, item, iIdx) => {
                    if (item?.optional) return sub;
                    const key = `${sIdx}_${iIdx}`;
                    const calc = itemCalculations[key] || { usages: {} };
                    return sub + Number(calc.usages?.[usageKey] || 0);
                  }, 0);
                }, 0);
                acc[usageKey] = total;
                return acc;
              }, {});

              return (
                <VStack align="stretch" spacing={4}>
                  <Flex align="center" gap={0} w="100%" sx={{
                    '& > *': { height: '48px', minHeight: '48px' }
                  }}>
                    {/* Left grouped columns with label under Description */}
                    <Box flex="1" display="flex" gap={0} alignItems="center">
                      <Box w="60px" />
                      <Box w="65px" />
                      <Box w="120px" />
                      <Box w="240px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="bold">{t('proposal.page.total') || 'TOTAL:'}</Text>
                      </Box>
                      <Box w="120px" /> {/* SPPU total not shown */}
                      <Box w="120px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="extrabold">{formatCurrency(totalSLT)}</Text>
                      </Box>
                    </Box>

                    {/* Usage totals aligned to their columns */}
                    {Object.entries(templateUsages).map(([usageKey]) => (
                      <Box key={usageKey} w="140px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="extrabold">{formatCurrency(usageTotals[usageKey] || 0)}</Text>
                      </Box>
                    ))}

                    {/* Remaining columns placeholders */}
                    <Box w="140px" />
                    <Box w="120px" />
                    <Box w="120px" />
                    <Box w="160px" />
                    <Box w="140px" />
                    <Box w="120px" />
                    <Box w="36px" />
                  </Flex>

                  {/* Cost overview table */}
                  <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>{t('proposal.page.deployments') || 'Deployments'}</Th>
                          <Th isNumeric>{t('proposal.page.costs') || 'Cost Overview'}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* SLT first */}
                        <Tr>
                          <Td>{t('table.slt') || 'SLT'}</Td>
                          <Td isNumeric>{formatCurrency(totalSLT)}</Td>
                        </Tr>
                        {Object.entries(templateUsages).map(([usageKey, usage], i) => (
                          <Tr key={usageKey}>
                            <Td>{(currentLanguage === 'de' ? (usage['lang.de'] || `Einsatz-${i+1}`) : (usage['lang.en'] || `Deployment-${i+1}`))}</Td>
                            <Td isNumeric>{formatCurrency(usageTotals[usageKey] || 0)}</Td>
                          </Tr>
                        ))}
                        <Tr fontWeight="bold">
                          <Td>{t('proposal.page.grand_total_deployments', { params: { count: 1 + Object.keys(templateUsages).length } }) || `Total for ${1 + Object.keys(templateUsages).length} deployments`}</Td>
                          <Td isNumeric>{formatCurrency(totalSLT + Object.values(usageTotals).reduce((a,b)=>a+Number(b||0),0))}</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Average per deployment */}
                  <HStack justify="space-between" bg="black" color="white" px={4} py={3} borderRadius="md">
                    <Text fontWeight="bold">{t('proposal.page.average_price_per_deployment') || 'Average price per deployment'}</Text>
                    <Text fontWeight="extrabold" fontSize="xl">
                      {formatCurrency(averagePricePerUsage)}
                    </Text>
                  </HStack>
                </VStack>
              );
            })()}
          </CardBody>
        </Card>
      )}

      {/* Footer expandable - en altta */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} mt={3}>
        <CardBody>
          <HStack justify="space-between" onClick={() => setFooterOpen(v=>!v)} cursor="pointer">
            <Text fontWeight="bold">Footer</Text>
          </HStack>
          <Collapse in={footerOpen} animateOpacity>
            <VStack mt={4} align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Footer Text</FormLabel>
                <FooterEditor value={meta.footer_text} onChange={(val)=> setMeta({...meta, footer_text: val})} />
              </FormControl>
            </VStack>
          </Collapse>
        </CardBody>
      </Card>
      {rowContextMenu.open && (
        <>
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={99998}
            onClick={() => setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null })}
          />
          <Box position="fixed" top={rowContextMenu.y} left={rowContextMenu.x} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" zIndex={99999} boxShadow="xl" minW="250px">
            <List spacing={0}>
              {(() => {
                const { sectionIndex, itemIndex } = rowContextMenu;
                const currentItem = sectionBlocks[sectionIndex]?.items[itemIndex];
                const isOptional = currentItem?.optional;

                const hasHidePriceNote = currentItem?.hide_price_note !== undefined && currentItem?.hide_price_note !== null && currentItem?.hide_price_note !== '';
                const hasProductNote = currentItem?.product_note !== undefined && currentItem?.product_note !== null && currentItem?.product_note !== '';

                return (
                  <>
                    {isOptional ? (
                      <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                        setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                          ...b,
                          items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: false, optional_hide_prices: false } : x)
                        } : b));
                        setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                      }}>
                        <HStack>
                          <Text color="red.500">✕</Text>
                          <Text>{t('proposal.actions.remove_optional', 'Remove optional status')}</Text>
                        </HStack>
                      </ListItem>
                    ) : (
                      <>
                        <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                          setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                            ...b,
                            items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: true, optional_hide_prices: false } : x)
                          } : b));
                          setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                        }}>
                          <Text>{t('proposal.actions.make_optional_with_prices', 'Make optional (with prices)')}</Text>
                        </ListItem>
                        <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                          setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                            ...b,
                            items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: true, optional_hide_prices: true } : x)
                          } : b));
                          setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                        }}>
                          <Text>{t('proposal.actions.make_optional_no_prices', 'Make optional (without prices)')}</Text>
                        </ListItem>
                      </>
                    )}
                    <Divider />
                    <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                      setNoteModal({ isOpen: true, type: 'hide_price_note', sectionIndex, itemIndex, value: currentItem?.hide_price_note || '' });
                      setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                    }}>
                      <Text color={hasHidePriceNote ? 'red.500' : undefined}>{hasHidePriceNote ? '✓ ' : ''}{t('proposal.actions.hide_price_add_note', 'Hide Price & Add Note')}</Text>
                    </ListItem>
                    <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                      setNoteModal({ isOpen: true, type: 'product_note', sectionIndex, itemIndex, value: currentItem?.product_note || '' });
                      setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                    }}>
                      <Text color={hasProductNote ? 'blue.500' : undefined}>{hasProductNote ? '✓ ' : ''}{t('proposal.actions.add_product_note', 'Add Product Note')}</Text>
                    </ListItem>
                  </>
                );
              })()}
            </List>
          </Box>
        </>
      )}

      {/* Note Modal (Hide Price Note / Product Note) */}
      <Modal isOpen={noteModal.isOpen} onClose={() => setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' })} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{noteModal.type === 'hide_price_note' ? t('proposal.actions.hide_price_add_note', 'Hide Price & Add Note') : t('proposal.actions.add_product_note', 'Add Product Note')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={noteModal.value}
              onChange={(e) => setNoteModal(prev => ({ ...prev, value: e.target.value }))}
              placeholder={noteModal.type === 'hide_price_note' ? t('proposal.actions.hide_price_note_prompt', 'Enter note (shown in red, price will be hidden)') : t('proposal.actions.product_note_prompt', 'Enter product note (shown bold below item)')}
              rows={3}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <HStack>
              {noteModal.value && (
                <Button variant="ghost" colorScheme="red" onClick={() => {
                  const { sectionIndex, itemIndex, type } = noteModal;
                  setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                    ...b,
                    items: b.items.map((x, k) => k === itemIndex ? { ...x, [type]: null } : x)
                  }) : b));
                  setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
                }}>
                  {t('common.remove') || 'Remove'}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' })}>{t('common.cancel') || 'Cancel'}</Button>
              <Button colorScheme="blue" onClick={() => {
                const { sectionIndex, itemIndex, type, value } = noteModal;
                setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                  ...b,
                  items: b.items.map((x, k) => k === itemIndex ? { ...x, [type]: value || null } : x)
                }) : b));
                setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
              }}>{t('common.save') || 'Save'}</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Item Modal */}
      <Modal isOpen={createItemModal.isOpen} onClose={() => setCreateItemModal({ isOpen: false, label: '', sectionIndex: null })} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('inventory.create_item', { defaultValue: 'Create item' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>{t('inventory.ask_add_stock_modal', { defaultValue: 'Do you want to add this to stock inventory or only to the proposal?' })}</Text>
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold">{createItemModal.label}</Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={3} flexWrap="wrap">
              <Button whiteSpace="nowrap" colorScheme="blue" onClick={async () => {
                try {
                  const name_en = createItemModal.label;
                  const name_de = createItemModal.label;
                  const res = await api.post('/api/inventory', {
                    name_en,
                    name_de,
                    description: '',
                    unit_price: 0,
                    stock_quantity: 0,
                    item_type: 'material',
                    uom: 'unit'
                  });
                  const createdId = res?.data?.data?.id || null;
                  const sIdx = createItemModal.sectionIndex;
                  setSectionBlocks(prev => prev.map((b, i) => i === sIdx ? {
                    ...b,
                    items: [
                      ...b.items,
                      {
                        item_id: createdId,
                        name: currentLanguage === 'de' ? name_de : name_en,
                        name_en,
                        name_de,
                        quantity: 1,
                        unit_price: 0,
                        uom_abbr: '',
                        item_type: 'product',
                        margin: null // Yeni ürün için margin boş
                      }
                    ]
                  } : b));
                  setCreateItemModal({ isOpen: false, label: '', sectionIndex: null });
                  toast({ title: t('common.success') || 'Success', status: 'success' });
                } catch (e) {
                  toast({ title: t('common.error') || 'Error', description: t('messages.server_error') || 'Server error', status: 'error' });
                }
              }}>
                {t('inventory.add_to_db', { defaultValue: 'Add to database' })}
              </Button>
              <Button whiteSpace="nowrap" colorScheme="gray" onClick={() => {
                const name_en = createItemModal.label;
                const name_de = createItemModal.label;
                const sIdx = createItemModal.sectionIndex;
                setSectionBlocks(prev => prev.map((b, i) => i === sIdx ? {
                  ...b,
                  items: [
                    ...b.items,
                    {
                      item_id: null,
                      name: currentLanguage === 'de' ? name_de : name_en,
                      name_en,
                      name_de,
                      quantity: 1,
                      unit_price: 0,
                      uom_abbr: '',
                      item_type: 'product',
                      margin: null // Yeni ürün için margin boş
                    }
                  ]
                } : b));
                setCreateItemModal({ isOpen: false, label: '', sectionIndex: null });
              }}>
                {t('inventory.do_not_add_to_db', { defaultValue: 'Do not add to database' })}
              </Button>
              <Button whiteSpace="nowrap" variant="outline" onClick={() => setCreateItemModal({ isOpen: false, label: '', sectionIndex: null })}>
                {t('common.cancel') || 'Cancel'}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Free Override Section */}
      {isReady && (
        <Card mt={4} variant="outline">
          <CardBody py={3}>
            <HStack spacing={4} align="center">
              <Checkbox
                isChecked={overrideEnabled}
                onChange={(e) => setOverrideEnabled(e.target.checked)}
                colorScheme="orange"
              >
                <Text fontWeight="medium">{t('proposal.override_total', 'Free Override')}</Text>
              </Checkbox>
              {overrideEnabled && (
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">{t('proposal.override_total_label', 'Custom Total')}:</Text>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={overrideTotal}
                    onChange={(e) => setOverrideTotal(e.target.value)}
                    placeholder="0.00"
                    w="150px"
                    size="sm"
                  />
                  <Text fontSize="sm" color="gray.500">EUR</Text>
                </HStack>
              )}
              {overrideEnabled && overrideTotal && (
                <Text fontSize="sm" color="orange.600" fontWeight="medium">
                  {t('proposal.override_note', 'Original total will be crossed out in PDF')}
                </Text>
              )}
            </HStack>
          </CardBody>
        </Card>
      )}

      {isReady && (
        <HStack justify="flex-end" mt={4}>
          <Button colorScheme="brand" onClick={() => {
            const sectionsPayload = sectionBlocks.map((b, sectionIdx) => ({
              section_id: b.section_id || null,
              name: b.section_name,
              margin: (b.margin === null || b.margin === undefined) ? 1.0 : (1 + (b.margin / 100)),
              order_index: sectionIdx, // Section sırası
              json_data: {
                margin: (b.margin === null || b.margin === undefined) ? 1.0 : (1 + (b.margin / 100)),
                section_name: b.section_name
              },
              items: (b.items || []).map((it, itemIdx) => {
                const calculationKey = `${sectionBlocks.indexOf(b)}_${b.items.indexOf(it)}`;
                const calculations = itemCalculations[calculationKey] || {
                  sppu: 0,
                  slt: 0,
                  final_price: 0,
                  unit_price_x_amount: 0,
                  usages: {}
                };
                return {
                  item_id: it.item_id || null,
                  name: it.name,
                  uom_abbr: it.uom_abbr || null,
                  order_index: itemIdx, // Item sırası
                  contractor_id: it.contractor_id || null,
                  contractor: it.contractor || null,
                  contractor_items: Array.isArray(it.contractor_items) ? it.contractor_items : [],
                  contractor_total: it.contractor_total !== undefined && it.contractor_total !== null ? Number(it.contractor_total) : null,
                  final_price: calculations.final_price,
                  unit_price_x_amount: calculations.unit_price_x_amount,
                  notes: it.notes || null,
                  sub_descriptions: Array.isArray(it.sub_descriptions) ? it.sub_descriptions : [],
                  optional: !!it.optional,
                  optional_hide_prices: !!it.optional_hide_prices,
                  description: null,
                  quantity: Number(it.quantity || 1),
                  unit_price: Number(it.unit_price || 0),
                  cost_type: 'per_use',
                  json_data: {
                    uom_abbr: it.uom_abbr || null,
                    // margin'i json_data'ya da koy (hesaplamalarda kullanılıyor)
                    margin: (it.margin === 0 || it.margin === null || it.margin === undefined) ? ( (b.margin === null || b.margin === undefined) ? 1.0 : (1 + (b.margin/100)) ) : (1 + (it.margin/100)),
                    contractor_id: it.contractor_id || null,
                    contractor: it.contractor || null,
                    contractor_items: Array.isArray(it.contractor_items) ? it.contractor_items : [],
                    contractor_total: it.contractor_total !== undefined && it.contractor_total !== null ? Number(it.contractor_total) : null,
                    notes: it.notes || null,
                    sub_descriptions: Array.isArray(it.sub_descriptions) ? it.sub_descriptions : [],
                    name_en: (it.name_en !== undefined && it.name_en !== null && String(it.name_en).trim() !== '') ? String(it.name_en).trim() : null,
                    name_de: (it.name_de !== undefined && it.name_de !== null && String(it.name_de).trim() !== '') ? String(it.name_de).trim() : null,
                    optional: !!it.optional,
                    optional_hide_prices: !!it.optional_hide_prices,
                    hide_price_note: it.hide_price_note || null,
                    product_note: it.product_note || null,
                    itemUsages: Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
                      const itemUsageKey = `${sectionBlocks.indexOf(b)}_${b.items.indexOf(it)}_${usageKey}`;
                      const customRatio = itemUsages[itemUsageKey]?.ratio;
                      if (customRatio !== undefined && customRatio !== usage.ratio) {
                        acc[usageKey] = customRatio;
                      }
                      return acc;
                    }, {})
                  }
                };
              })
            }));
            
            // Ortalama fiyat hesapla (usage toplamlarinin ortalamasi)
            const usageKeys = Object.keys(templateUsages || {});
            const usageTotals = usageKeys.reduce((acc, key) => {
              const total = sectionBlocks.reduce((sum, section, sIdx) => {
                return sum + (section.items || []).reduce((sub, item, iIdx) => {
                  if (item?.optional) return sub;
                  const calc = itemCalculations[`${sIdx}_${iIdx}`] || { usages: {} };
                  return sub + Number(calc.usages?.[key] || 0);
                }, 0);
              }, 0);
              acc[key] = total; return acc;
            }, {});
            const avgPerDeployment = (() => {
              const totals = Object.values(usageTotals);
              if (!totals.length) return Number(sectionBlocks.reduce((sum, section, sIdx)=>{
                return sum + (section.items||[]).reduce((sub,item,iIdx)=>{
                  if (item?.optional) return sub;
                  const calc = itemCalculations[`${sIdx}_${iIdx}`] || { slt: 0 };
                  return sub + Number(calc.slt||0);
                },0);
              },0));
              const sum = totals.reduce((a,b)=>a+Number(b||0),0);
              return sum / totals.length;
            })();

            const payload = {
              title: title || `${selection.client_name || ''} - v${version || '1'}`,
              description: meta.subject || '', // Subject is stored in description field
              client_id: Number(selection.client_id),
              valid_until: null,
              status: 'draft',
              currency: '',
              version_number: Number(version || 1),
              version_note: versionNote || null,
              template_id: Number(selection.template_id),
              proposal_type: proposalType, // 'offer' or 'proforma'
              show_detailed_pricing: showDetailedPricing ? 1 : 0,
              override_enabled: overrideEnabled ? 1 : 0,
              override_total: overrideEnabled && overrideTotal ? Number(overrideTotal) : null,
              template_json: {
                usages: templateUsages,
                sections: sectionBlocks.map(b => ({
                  section_id: b.section_id,
                  name: b.section_name,
                  margin: (b.margin === null || b.margin === undefined) ? 1.0 : (1 + (b.margin / 100))
                }))
              },
              project: meta.project || null,
              offer_status: meta.offer_status || null,
              in_charge: Array.isArray(meta.in_charge) && meta.in_charge.length > 0 ? JSON.stringify(meta.in_charge) : null,
              notes: meta.notes || null,
              footer_text: (meta.footer_text && meta.footer_text.trim().length > 0) ? meta.footer_text : null,
              footer_signature_left: meta.footer_signature_left || null,
              footer_signature_right: meta.footer_signature_right || null,
              sections: sectionsPayload,
              average_price_per_deployment: Number(avgPerDeployment || 0),
              items: sectionsPayload.flatMap(s => s.items)
            };
            
            console.log('💾 Save payload:', payload);
            console.log('🔍 Sample item with optional:', payload.items.find(item => item.optional));
            createProposalMutation.mutate(payload);
          }} isLoading={createProposalMutation?.isLoading}>
            {t('common.save') || 'Save'}
        </Button>
      </HStack>
      )}

      {/* New Supplier Modal */}
      <Modal isOpen={newSupplierModal} onClose={() => {
        setNewSupplierModal(false);
        setNewSupplierForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          category: '',
          rating: '',
          notes: ''
        });
      }} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('common.new')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input value={newSupplierForm.name} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, name: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.email')}</FormLabel>
                  <Input value={newSupplierForm.email} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, email: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.phone')}</FormLabel>
                  <Input value={newSupplierForm.phone} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, phone: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.address') || 'Address'}</FormLabel>
                <Textarea value={newSupplierForm.address} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, address: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.category') || 'Category'}</FormLabel>
                  <Input value={newSupplierForm.category} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, category: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.rating') || 'Rating'}</FormLabel>
                  <Input type="number" value={newSupplierForm.rating} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, rating: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.notes') || 'Notes'}</FormLabel>
                <Textarea value={newSupplierForm.notes} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, notes: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => {
              setNewSupplierModal(false);
              setNewSupplierForm({
                name: '',
                email: '',
                phone: '',
                address: '',
                category: '',
                rating: '',
                notes: ''
              });
            }}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={saveNewSupplier}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Contractor Modal */}
      <Modal isOpen={contractorModal.isOpen} onClose={() => {
        setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null });
        setContractorSearch('');
        setContractorSuggestions([]);
        setShowContractorSuggestions(false);
      }} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{t('table.contractor') || 'Contractor'} Management</Text>
              <Text fontSize="sm" color="gray.500">- Row {contractorModal.itemIndex !== null ? contractorModal.itemIndex + 1 : ''}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Contractor Selection */}
              <Box position="relative">
                <FormLabel fontWeight="bold" mb={2}>{t('table.contractor') || 'Contractor'}</FormLabel>
                <HStack>
                  <Input
                    placeholder="Search contractor name..."
                    value={contractorSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContractorSearch(value);
                      setSelectedContractor(value);
                      searchContractors(value);
                    }}
                    onFocus={() => {
                      if (contractorSearch.length >= 2) {
                        setShowContractorSuggestions(true);
                      }
                    }}
                    size="md"
                  />
                  <Button
                    colorScheme="brand"
                    size="md"
                    onClick={() => setNewSupplierModal(true)}
                    leftIcon={<Text fontSize="lg">+</Text>}
                  >
                    {t('common.new')}
                  </Button>
                </HStack>
                
                {/* Contractor Suggestions Dropdown */}
                {showContractorSuggestions && contractorSuggestions.length > 0 && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    zIndex={1000}
                    maxH="200px"
                    overflowY="auto"
                  >
                    {contractorSuggestions.map((contractor) => (
                      <Box
                        key={contractor.id}
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        onClick={() => selectContractor(contractor)}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Text fontWeight="medium">{contractor.name}</Text>
                        {contractor.email && (
                          <Text fontSize="sm" color="gray.600">{contractor.email}</Text>
                        )}
                        {contractor.category && (
                          <Text fontSize="xs" color="blue.600">Category: {contractor.category}</Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Contractor Items Table */}
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="bold">{t('proposals.items') || 'Items'}</Text>
                  <Button size="sm" colorScheme="blue" onClick={addContractorItem} leftIcon={<FiPlus />}>
                    {t('common.add') || 'Add'} Item
                  </Button>
                </HStack>
                
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>{t('table.description') || 'Description'}</Th>
                        <Th>{t('table.qty') || 'Qty'}</Th>
                        <Th>{t('table.unit_price') || 'Unit Price'}</Th>
                        <Th>{t('table.final_price') || 'Total'}</Th>
                        <Th w="80px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contractorItems.map((item, index) => (
                        <Tr key={index}>
                          <Td>
                            <Input
                              value={item.name}
                              onChange={(e) => updateContractorItem(index, 'name', e.target.value)}
                              placeholder="Item/Service name..."
                              size="sm"
                            />
                          </Td>
                          <Td>
                            <NumberInput
                              value={item.quantity}
                              onChange={(value) => updateContractorItem(index, 'quantity', Number(value))}
                              min={1}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <NumberInput
                              value={item.unit_price}
                              onChange={(value) => updateContractorItem(index, 'unit_price', Number(value))}
                              min={0}
                              step={0.01}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <Text fontWeight="medium" color="blue.600">
                              {formatCurrency(item.total_price || 0)}
                            </Text>
                          </Td>
                          <Td>
                            <IconButton
                              aria-label="remove item"
                              icon={<FiX />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeContractorItem(index)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {/* Total Calculation */}
                {contractorItems.length > 0 && (
                  <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="blue.700">
                        {t('table.final_price') || 'Total Amount'}:
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="blue.700">
                        {formatCurrency(contractorItems.reduce((sum, item) => sum + (item.total_price || 0), 0))}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={() => setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null })}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button colorScheme="blue" onClick={saveContractorData} isDisabled={contractorItems.length === 0}>
                {t('common.save') || 'Save'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProposalCreate;



